import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import FileUpload from '@/components/FileUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', event_type: 'Wedding', quote: '', image_url: '', rating: 5 });

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setTestimonials(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name, event_type: item.event_type, quote: item.quote, image_url: item.image_url, rating: item.rating });
    } else {
      setEditingItem(null);
      setFormData({ name: '', event_type: 'Wedding', quote: '', image_url: '', rating: 5 });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.quote) { toast.error('Fill required fields'); return; }
    setIsSaving(true);
    const token = getAuthToken();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/testimonials/${editingItem.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/testimonials`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Saved!');
      setIsDialogOpen(false);
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed');
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Testimonials</h1>
        <Button onClick={() => openDialog()} className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">
          <Plus size={18} className="mr-2" /> Add Testimonial
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {testimonials.map((item, index) => (
          <div key={item.id} className="bg-white rounded-2xl shadow-soft p-6">
            <div className="flex items-start gap-4 mb-4">
              <img src={item.image_url} alt={item.name} className="w-16 h-16 rounded-full object-cover" />
              <div>
                <h3 className="font-heading text-xl text-[#2C2C2C]">{item.name}</h3>
                <p className="text-sm text-[#6B7280]">{item.event_type}</p>
                <div className="flex gap-0.5 mt-1">{[...Array(item.rating)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />)}</div>
              </div>
            </div>
            <p className="text-[#6B7280] italic mb-4 line-clamp-3">"{item.quote}"</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openDialog(item)} className="border-[#E8DFD0]"><Pencil size={14} className="mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 border-red-200"><Trash2 size={14} className="mr-1" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editingItem ? 'Edit' : 'Add'} Testimonial</DialogTitle></DialogHeader>
          <div className="space-y-6 mt-4">
            <div><Label className="text-sm font-medium mb-2 block">Client Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="input-elegant" placeholder="e.g., Sarah & Michael" /></div>
            <div><Label className="text-sm font-medium mb-2 block">Event Type</Label>
              <Select value={formData.event_type} onValueChange={(v) => setFormData(p => ({ ...p, event_type: v }))}>
                <SelectTrigger className="input-elegant"><SelectValue /></SelectTrigger>
                <SelectContent>{['Wedding', 'Anniversary', 'Dinner Party', 'Engagement Party', 'General Party'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-medium mb-2 block">Quote *</Label><Textarea value={formData.quote} onChange={(e) => setFormData(p => ({ ...p, quote: e.target.value }))} className="input-elegant min-h-[100px]" /></div>
            <div>
              <FileUpload currentUrl={formData.image_url} onUpload={(url) => setFormData(p => ({ ...p, image_url: url }))} accept="image/*" label="Upload Client Photo" />
              <div className="mt-2"><Label className="text-sm font-medium mb-2 block">Or paste Photo URL</Label><Input value={formData.image_url} onChange={(e) => setFormData(p => ({ ...p, image_url: e.target.value }))} className="input-elegant" /></div>
            </div>
            <div><Label className="text-sm font-medium mb-2 block">Rating</Label>
              <Select value={String(formData.rating)} onValueChange={(v) => setFormData(p => ({ ...p, rating: Number(v) }))}>
                <SelectTrigger className="input-elegant"><SelectValue /></SelectTrigger>
                <SelectContent>{[5, 4, 3, 2, 1].map(n => <SelectItem key={n} value={String(n)}>{n} Stars</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-4 pt-4">
              <Button onClick={() => setIsDialogOpen(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-white">{isSaving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestimonials;
